/* eslint-disable */
import React, { FC, ReactNode } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useHistory } from 'react-router';
import styled, { css } from 'styled-components';
import colors from '~/constants/colors';
import theme from '~/constants/theme';
import paths from '~/pages/paths';
import { LanguageSelector, Space } from '~/components';
import { isAgent, isMerchant } from '~/utils/getDecodedJwt';
import direction from '~/utils/direction';
import { useDirection } from '~/hooks/i18nHooks';
import { Direction } from '~/types/Direction';
import { LogoWithTextImg } from '~/img';
import { Button, Row, Col, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { MobileOutlined, PhoneOutlined } from '@ant-design/icons';
import LanguageMenu from '~/components/LanguageSelector/LanguageMenu';
import UaeHeaderLogout from '~/layouts/uae/UaeHeaderLogout';
import UaeMobileMenu from '~/layouts/uae/UaeMobileMenu';
import { useGetApplication } from '~/controllers/wizard';

interface Props {
  middleContent?: ReactNode;
  tabletContent?: ReactNode;
  onBoarding?: boolean;
  isServices?: boolean;
}

const Header: FC<Props> = (props) => {
  const { middleContent, tabletContent, onBoarding, isServices } = props;
  const history = useHistory();
  const applicationQuery: any = useGetApplication();
  const applicationNumber = applicationQuery?.data?.applicationDisplayId ?? '';
  const dir = useDirection();
  const { t } = useTranslation('ksaApplication');
  const goHome = () => {
    if (process.env.NODE_ENV === 'development') {
      history?.push(paths.home._);
      if (isMerchant()) applicationQuery?.refetch?.();
    }
  };

  const isMobile = useMediaQuery({ maxWidth: theme.breakpoints.sm });
  const middleContentBottom = useMediaQuery({
    maxWidth: isAgent() ? theme.breakpoints.md : theme.breakpoints.sm,
  });
  const headerHeight = isMobile ? 72 : 80;
  return (
    <HeaderContainer
      isMobile={isMobile}
      isServices={isServices}
      onBoarding={onBoarding}
      className="Header"
      headerHeight={headerHeight}
      dir={dir}
      middleContentBottom={middleContentBottom}
    >
      {isAgent() && (
        <Typography.Paragraph className="applicationNumberHeader">
          {t('Application Number :')} {applicationNumber}
        </Typography.Paragraph>
      )}
      <div className="Header-Content">
        <div className="Header-LeftContainer Header-CornerContainer">
          {
            <Space size={24} fullWidth={false}>
              {isMobile ? tabletContent : null}
              <LogoWithTextImg
                className={isMobile ? 'logo-mobile' : 'logo-desktop'}
                onClick={goHome}
              />
            </Space>
          }
        </div>
        {middleContentBottom ? null : middleContent}
        {isMobile ? (
          <Row justify="end" align="middle" gutter={[12, 12]}>
            <Col>
              <LanguageSelector />
            </Col>
            <Col>
              <UaeMobileMenu />
            </Col>
          </Row>
        ) : (
          <div className="Header-RightContainer Header-CornerContainer">
            {!isMobile && !isAgent() && (
              <Row justify="end" align="middle" gutter={[20, 0]}>
                <Col>
                  <LanguageSelector />
                </Col>
                <Col>
                  <Button
                    onClick={() => {
                      history.push(paths.ksa.contactUs);
                    }}
                    className="getSupport-btn"
                  >
                    {t('Contact Us')}
                  </Button>
                </Col>
                <Col>
                  <UaeHeaderLogout />
                </Col>
              </Row>
            )}
          </div>
        )}
      </div>
      {middleContentBottom && middleContent ? (
        <div className="Header-MiddleContent__mobile">{middleContent}</div>
      ) : null}
    </HeaderContainer>
  );
};

interface UIProps {
  headerHeight: number;
  dir: Direction;
  middleContentBottom: boolean;
  onBoarding?: boolean;
  isServices?: boolean;
  isMobile?: boolean;
}

const HeaderContainer = styled.header<UIProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: sticky;
  padding: 0 10% 0 5%;
  // width: 80%;
  // margin:auto;
  top: 0;
  z-index: 90;

  @media (max-width: ${theme.breakpoints.sm}px) {
    padding: 0 16px;
  }

  .applicationNumberHeader {
    align-items: center;
    justify-content: center;
    text-align: center;
    margin-top: 12px;
    color: #000;
    font-family: Inter, sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
  }
  background-color: ${({ onBoarding, isServices, isMobile }) =>
    onBoarding || (isServices && isMobile) ? 'rgba(197, 228, 249, 1)' : 'transparent'};

  /* Contact Us button — matches Figma */
  .getSupport-btn {
    border-radius: 6px;
    background: #0069b1;
    box-shadow: 0 2px 0 0 rgba(0, 89, 150, 0.10);
    color: #fff;
    font-weight: 600;
    font-size: 14px;
    border-color: #0069b1;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    height: auto;
    padding: 12px 16px;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    z-index: 5;
    &:hover,
    &:focus {
      background: #005a99;
      border-color: #005a99;
      color: #fff;
    }
  }

  // box-shadow applies by index.less
  transition: box-shadow 0.2s ease-out;
  height: ${({ headerHeight }) => headerHeight}px;
  .Header-Content {
    display: flex;
    flex: 1;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    height: 100%;
    .Header-CornerContainer {
      display: flex;
      flex: 1;
      .HeaderCompanyName-LegalName {
        max-width: ${320 - (16 + 58)}px;
      }
    }
    .Header-RightContainer {
      justify-content: flex-end;
    }
    .Header-LeftContainer {
      display: flex;
      align-items: center;
      .HeaderCompanyName {
        ${direction.margin.left(21)};
      }
      /* Desktop logo — height 38px per Figma */
      .logo-desktop {
        // height: 38px;
        width: auto;
        cursor: pointer;
        align-self: stretch;
      }
      /* Mobile logo — 100×20px per Figma */
      .logo-mobile {
        height: 20px;
        width: 100px;
        cursor: pointer;
      }
      .Header-LogoWithTextImg {
        height: ${theme.logoWithTextHeight}px;
        cursor: pointer;
      }
      .Header-LogoImg {
        height: ${theme.logoHeight}px;
      }
    }
  }
  .Header-BurgerMenu {
    color: ${colors.primaryColor};
    font-size: 36px;
    cursor: pointer;
  }
  .Header-MiddleContent__mobile {
    margin-top: 16px;
  }
  @media (max-width: 1480px) {
    .Header-Content {
      .Header-CornerContainer {
        .HeaderCompanyName-LegalName {
          max-width: calc(24vw - ${52 + 21 + 48}px);
        }
      }
    }
  }
  @media (max-width: ${theme.breakpoints.lg}px) {
    .Header-Content {
      .Header-CornerContainer {
        .HeaderCompanyName-LegalName {
          max-width: calc(31vw - ${52 + 21 + 48}px);
        }
      }
    }
  }
  @media (max-width: ${theme.breakpoints.md}px) {
    .Header-Content {
      .Header-CornerContainer {
        .HeaderCompanyName-LegalName {
          max-width: calc(100vw - ${32 + 52 + 21 + 200}px);
        }
      }
    }
  }
  /* Mobile-specific overrides */
  @media (max-width: ${theme.breakpoints.sm}px) {
    /* Language selector on mobile — Figma: padding 4px 12px, border-radius 20px, white background */
    .LanguageSelector.ant-btn {
      padding: 4px 12px !important;
      height: auto !important;
      min-height: unset !important;
      font-size: 14px !important;
      border-radius: 20px !important;
      background: #ffffff !important;
      background-color: #ffffff !important;
      border: none !important;
      box-shadow: none !important;
      gap: 8px !important;
      display: flex !important;
      align-items: center !important;
      color: #1a1a1a !important;

      .LanguageSelector-GlobeIcon {
        font-size: 24px !important;
        width: 24px !important;
        height: 24px !important;
      }

      &:hover,
      &:focus,
      &:active {
        background: #f0f0f0 !important;
        background-color: #f0f0f0 !important;
        box-shadow: none !important;
        border: none !important;
        color: #1a1a1a !important;
      }
    }
  }

  /* Desktop language selector — Figma: padding 8px 16px, border-radius 6px, gap 8px */
  @media (min-width: ${theme.breakpoints.sm + 1}px) {
    .LanguageSelector {
      padding: 8px 16px;
      height: auto;
      border-radius: 6px;
      // gap: 8px;

      .LanguageSelector-GlobeIcon {
        font-size: 20px;
      }
    }
  }
  ${({ middleContentBottom }) =>
    middleContentBottom
      ? css`
          .Header-Content {
            .Header-CornerContainer {
              flex: unset;
            }
          }
        `
      : undefined}
`;

export default Header;
